import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../components/Modal';
import './DeviceManager.css';
import './RefreshBar.css';
import { api } from '../config/api';

// Refresh bar constants
const REFRESH_INTERVAL_MS = 100000000;
const PROGRESS_UPDATE_MS = 100000;    //10 seconds update progress bar every 100ms
const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';

// Matches your /devices schema from Swagger
interface Device {
  deviceId: string;
  deviceDisplayId: string;
  lot: {
    lotId: string;
  };
  deviceType: string;
  isWifiRegistered: boolean;
  wifiNetworkName: string;
  wifiPassword: string;
  ipAddress: string;
  deviceStatus: string;   // e.g. "Online", "Offline", "RecentlyAdded", ...
  lastActive: string;
  deviceTemp: number;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  isDeleted: boolean;
}

// Internally for easier rendering
interface ParsedDevice {
  id: string;
  backendId: string;
  displayId: string;
  statusText: string;       // e.g. "Online 32°C" or "Offline"
  updatedText: string;      // e.g. "Recently Added • 3 mins ago"
  network: string;
  ipAddress: string;
  isOnline: boolean;
  colorClass: 'online' | 'calibrating' | 'offline';
}

// New helper to format device display IDs
const formatDisplayId = (rawDisplayId: string): string => {
  // Remove the 'D-' prefix if present
  const withoutPrefix = rawDisplayId.startsWith('D-') ? rawDisplayId.slice(2) : rawDisplayId;
  // Take the last 9 characters
  const lastNine = withoutPrefix.slice(-9);
  // Insert a dash between the 4th and 5th characters
  return lastNine.slice(0, 4) + '-' + lastNine.slice(4);
};

const DeviceManager: React.FC = () => {
  // URL param: /lot/:lotId/device-manager
  const { lotId } = useParams<{ lotId: string }>();

  const [devices, setDevices] = useState<Device[]>([]);
  const [displayLotId, setDisplayLotId] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'add' | 'remove' | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [nextDeviceId, setNextDeviceId] = useState<string | null>(null);

  // Auto-refresh progress bar
  const [progress, setProgress] = useState(0);

  // 1) Fetch devices on mount
  useEffect(() => {
    fetchLotData();
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Auto-refresh using the progress bar
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          // Time to fetch
          fetchDevices();
          return 0;
        }
        // step so we reach 100% in REFRESH_INTERVAL_MS
        const step = 100 / (REFRESH_INTERVAL_MS / PROGRESS_UPDATE_MS);
        return prev + step;
      });
    }, PROGRESS_UPDATE_MS);

    return () => clearInterval(timer);
  }, [progress]);

  // Fetch lot data to get displayLotId
  const fetchLotData = async () => {
    if (!lotId) return;
    try {
      const response = await api.get('/parkinglots/get-all-parking-lots');
      const data = response.data;
      if (data && data.data) {
        const allLots = data.data;
        const foundLot = allLots.find((lot: any) => lot.lotId === lotId);
        if (foundLot) {
          setDisplayLotId(foundLot.displayLotId || foundLot.lotId);
        } else {
          setDisplayLotId(lotId || "");
        }
      }
    } catch (error) {
      console.error('Error fetching lot data:', error);
      setDisplayLotId(lotId || "");
    }
  };

  // Format lot ID by removing the prefix if present
  const formatLotId = (lotId: string): string => {
    const prefix = "PWP-PL-";
    if (lotId.startsWith(prefix)) {
      return lotId.substring(prefix.length);
    }
    return lotId;
  };

  // Format device ID by removing the prefix if present
  const formatDeviceId = (deviceId: string): string => {
    const prefix = "PWP-D-";
    if (deviceId.startsWith(prefix)) {
      return deviceId.substring(prefix.length);
    }
    return deviceId;
  };

  // GET /devices => filter by lotId
  const fetchDevices = async () => {
    if (!lotId) return; // if no lotId in the URL, skip
    try {
      // Use the get-by-lot endpoint with the lot ID
      const response = await api.get(`/devices/get-by-lot/${lotId}`);
      const data = response.data;
      if (data.success && data.data) {
        setDevices(data.data);
        calculateNextDeviceId(data.data);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  // Figure out the next deviceId by checking existing devices and their deleted status
  const calculateNextDeviceId = (lotDevices: Device[]) => {
    if (!lotId || !displayLotId) {
      setNextDeviceId(null);
      return;
    }

    // Helper function to extract numeric part from lot display ID
    const getLotNumber = (displayLotId: string): number => {
      // Remove L- prefix if present  
      const cleanLotId = displayLotId.startsWith('L-') ? displayLotId.substring(2) : displayLotId;
      // Extract numeric part from the cleaned display ID
      const match = cleanLotId.match(/\d+/);
      return match ? parseInt(match[0], 10) : 1;
    };

    // Get all device display IDs and their deleted status
    const deviceInfo = lotDevices.map(d => ({
      displayId: d.deviceDisplayId,
      isDeleted: d.isDeleted
    }));

    // Extract the numeric part and letter from each display ID
    const devicePatterns = deviceInfo.map(info => {
      const match = info.displayId.match(/D-(\d+)([a-z]+)/);
      if (!match) return null;
      return {
        number: parseInt(match[1]),
        letter: match[2],
        isDeleted: info.isDeleted
      };
    }).filter(Boolean);

    // Sort by number and letter
    devicePatterns.sort((a, b) => {
      if (a!.number !== b!.number) return a!.number - b!.number;
      return a!.letter.localeCompare(b!.letter);
    });

    // First, check for any deleted devices that we can reuse
    const deletedDevice = devicePatterns.find(p => p!.isDeleted);
    if (deletedDevice) {
      const formattedNumber = String(deletedDevice.number).padStart(12, '0');
      setNextDeviceId(`D-${formattedNumber}${deletedDevice.letter}`);
      return;
    }

    // If no deleted devices, find the first missing letter in the alphabet sequence
    const usedLetters = new Set(devicePatterns.map(p => p!.letter));
    let nextLetter = null;
    let nextNumber = null;
    for (let i = 0; i < ALPHABET.length; i++) {
      if (!usedLetters.has(ALPHABET[i])) {
        nextLetter = ALPHABET[i];
        // Use the same number as the first device if it exists, else derive from lot display ID
        nextNumber = devicePatterns.length > 0 ? devicePatterns[0]!.number : getLotNumber(displayLotId);
        break;
      }
    }

    // If all letters are used, increment the number and start with 'a'
    if (!nextLetter) {
      nextNumber = (devicePatterns.length > 0 ? devicePatterns[0]!.number : getLotNumber(displayLotId)) + 1;
      nextLetter = 'a';
    }

    // Format the next device ID
    if (nextNumber !== null && nextLetter !== null) {
      const formattedNumber = String(nextNumber).padStart(12, '0');
      setNextDeviceId(`D-${formattedNumber}${nextLetter}`);
    } else {
      setNextDeviceId(null);
    }
  };

  // Recalculate next device ID when displayLotId changes
  useEffect(() => {
    if (displayLotId && devices.length >= 0) {
      calculateNextDeviceId(devices);
    }
  }, [displayLotId, devices]);

  // Manual refresh if user clicks the "Refreshing..." text
  const handleManualRefresh = () => {
    fetchDevices();
    setProgress(0);
  };

  // POST /devices => add a new device
  const handleAddDevice = async () => {
    if (!nextDeviceId || !lotId) {
      console.error('No next device ID or missing lotId');
      return;
    }

    try {
      // First, check if there's a deleted device with this display ID
      const response = await api.get(`/devices/get-all-by-lot/${lotId}`);
      const data = response.data;
      if (data.success && data.data) {
        const deletedDevice = data.data.find((d: Device) =>
          d.deviceDisplayId === nextDeviceId && d.isDeleted
        );

        if (deletedDevice) {
          // If found, restore the deleted device
          const restoreResponse = await api.put(`/devices/restore-device/${lotId}/${deletedDevice.deviceId}?restoredBy=superadmin`);
          console.log('Device restored successfully');
        } else {
          // If no deleted device found, create a new one
          const newDevice = {
            lotID: lotId,
            deviceType: 'ALPR',
            wifiNetworkName: null,
            wifiPassword: null,
            ipAddress: null,
            createdBy: 'superadmin',
          };
          const createResponse = await api.post('/devices/create-device', newDevice);
          console.log('New device created successfully');
        }
        // refresh list
        fetchDevices();
      }
    } catch (error) {
      console.error('Error in device operation:', error);
    }
  };

  // DELETE /devices/{deviceId}/{deletedBy}
  const handleRemoveDevice = async (deviceDisplayId: string) => {
    try {
      // Find the device by displayId to get its actual deviceId
      const device = devices.find(d => d.deviceDisplayId === deviceDisplayId);
      if (!device) {
        console.error('Device not found');
        return;
      }

      // Use the device's actual deviceId for deletion and include the deletedBy parameter
      const resp = await api.delete(`/devices/delete/${device.deviceId}/superadmin`);
      console.log('Device removed successfully');
      // refresh
      fetchDevices();
    } catch (error) {
      console.error('Error removing device:', error);
    }
  };

  // Modal openers
  const openAddModal = () => {
    setModalAction('add');
    setIsModalOpen(true);
  };
  const openRemoveModal = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    setModalAction('remove');
    setIsModalOpen(true);
  };

  // Modal "confirm" => calls add or remove
  const handleConfirmModal = () => {
    if (modalAction === 'add') {
      handleAddDevice();
    } else if (modalAction === 'remove' && selectedDeviceId) {
      handleRemoveDevice(selectedDeviceId);
    }
    setIsModalOpen(false);
  };
  const handleCancelModal = () => setIsModalOpen(false);

  // Utility: how long ago was lastActive
  const getRelativeTime = (timestamp: string | null): string => {
    if (!timestamp) return 'No activity';
    const parsedDate = new Date(timestamp);
    if (isNaN(parsedDate.getTime())) return 'Invalid date';

    const now = new Date();
    const diffMs = now.getTime() - parsedDate.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHr = Math.round(diffMin / 60);
    const diffDays = Math.round(diffHr / 24);

    if (diffSec < 60) return `${diffSec} seconds ago`;
    if (diffMin < 60) return `${diffMin} mins ago`;
    if (diffHr < 24) return `${diffHr} hours ago`;
    if (diffDays <= 7) return `${diffDays} days ago`;
    // fallback: e.g. "September 18, 2:12 PM"
    return parsedDate.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Convert from raw Device -> info for rendering
  const parseDevice = (device: Device): ParsedDevice => {
    const { deviceDisplayId, deviceStatus, deviceTemp, wifiNetworkName, ipAddress, lastActive } = device;

    let colorClass: 'online' | 'calibrating' | 'offline' = 'offline';
    if (deviceStatus === 'Online') colorClass = 'online';
    else if (deviceStatus === 'Calibrating') colorClass = 'calibrating';
    else if (deviceStatus === 'RecentlyAdded') {
      colorClass = 'offline'; // treat as offline color
    }

    // status text
    let statusText = deviceStatus;
    if (deviceStatus === 'Online' && deviceTemp !== 0) {
      statusText = `Online ${deviceTemp}°C`;
    } else if (deviceStatus === 'RecentlyAdded') {
      // We display "Offline" but keep deviceStatus for color
      statusText = 'Offline';
    }

    // updated text
    let updatedText = getRelativeTime(lastActive);
    if (deviceStatus === 'RecentlyAdded') {
      updatedText = `Recently Added • ${updatedText}`;
    }

    // Derive backendId:
    // If device.deviceId starts with 'DEVICE', use it directly
    // Otherwise, derive from deviceDisplayId. Expected deviceDisplayId format: "D-000000000001c"
    // We'll extract the numeric part (ignoring the leading "D-" and trailing letter), then add 1 and format with prefix "DEVICE" and 5-digit padding.
    let backendId = device.deviceId;
    if (!backendId.startsWith('DEVICE')) {
      const match = deviceDisplayId.match(/^D-0*(\d+)[a-z]$/);
      if (match) {
        const num = parseInt(match[1], 10);
        backendId = 'DEVICE' + String(num + 1).padStart(5, '0');
      }
    }

    return {
      id: formatDisplayId(deviceDisplayId),
      backendId,
      displayId: deviceDisplayId,
      statusText,
      updatedText,
      network: wifiNetworkName,
      ipAddress: ipAddress || 'N/A',
      isOnline: deviceStatus === 'Online',
      colorClass
    };
  };

  return (
    <div className="device-manager">
      {/* Refresh bar */}
      <div className="refresh-bar-wrapper">
        <div className="refresh-bar">
          <div className="refresh-bar-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="refresh-text" onClick={handleManualRefresh}>
          Refresh
        </div>
      </div>

      <h1>Device Manager</h1>

      <div className="device-list">
        {devices
          .sort((a, b) => formatDeviceId(a.deviceDisplayId).localeCompare(formatDeviceId(b.deviceDisplayId)))
          .map((device, idx) => {
            const parsed = parseDevice(device);
            return (
              <div
                key={parsed.id}
                className={`device ${parsed.colorClass}`}
                style={{
                  backgroundColor: idx % 2 === 0 ? '#363941' : '#2B2E35',
                }}
              >
                <div className="device-info">
                  <h2>{parsed.id}</h2>
                  <p className="status">{parsed.statusText}</p>
                  <p className="updated">{parsed.updatedText}</p>
                </div>
                <div className="device-actions">
                  <div className="action">
                    <button disabled={!parsed.isOnline}>
                      <img src="/assets/power.svg" alt="Shutdown Icon" />
                    </button>
                    <span>Shutdown</span>
                  </div>
                  <div className="action">
                    <button disabled={!parsed.isOnline}>
                      <img src="/assets/reboot.svg" alt="Reboot Icon" />
                    </button>
                    <span>Reboot</span>
                  </div>
                </div>
                <div className="network-info">
                  <p>
                    Network
                    <br />
                    <a
                      href={`http://${parsed.ipAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ip-link"
                    >
                      {parsed.ipAddress}
                    </a>
                  </p>
                </div>
                <div className="remove-device">
                  <button onClick={() => openRemoveModal(parsed.displayId)}>
                    <img src="/assets/Minus.svg" alt="Remove Device" />
                  </button>
                </div>
              </div>
            );
          })}

        {/* "Add Device" tile */}
        <div
          className="add-new-device"
          onClick={openAddModal}
          style={{
            backgroundColor: devices.length % 2 === 0 ? '#363941' : '#2B2E35',
          }}
        >
          <div className="device-info">
            <h2>{nextDeviceId ? formatDisplayId(nextDeviceId) : 'New Device'}</h2>
            <p className="status">+ Add New Device</p>
          </div>
          <div className="add-device">
            <button>
              <img src="/assets/Plus.svg" alt="Add Device" />
            </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <Modal
          title={
            modalAction === 'add'
              ? 'Confirm adding a new device'
              : 'Confirm removing this device'
          }
          message={
            modalAction === 'add'
              ? 'It will appear as "RecentlyAdded" until configured.'
              : 'Removing a device stops its data reporting. You can re-add later.'
          }
          confirmText={modalAction === 'add' ? 'Add Device' : 'Remove Device'}
          cancelText="Cancel"
          onConfirm={handleConfirmModal}
          onCancel={handleCancelModal}
        />
      )}
    </div>
  );
};

export default DeviceManager;
